import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  fetchTestScenario,
  createTestCase,
  createTestStep,
  updateTestCase,
  updateTestStep,
  updateTestCaseExecution,
  updateTestStepExecution,
  updateTestScenario,
  fetchProjectLabels,
  fetchTestScenarioLabels,
  addTestScenarioLabel,
  removeTestScenarioLabel,
  fetchTestRunCustomFields,
  createTestRun,
  updateTestRun,
  deleteTestRun,
  clearTestRun,
  createCustomField,
  updateCustomField,
  deleteCustomField,
  deleteTestCase,
  deleteTestStep
} from '../../api/testScenarioApi'
import {
  fetchLinkedIssuesForScenario,
  linkJiraIssueToScenario,
  unlinkScenario as unlinkJiraIssueApi
} from '../../api/issueLinkApi'
import { transformApiData } from '../../utils/dataTransformer'

// Async thunk for fetching test scenario data
export const fetchTestScenarioData = createAsyncThunk(
  'testScenario/fetchTestScenarioData',
  async (scenarioId, { rejectWithValue }) => {
    try {
      const data = await fetchTestScenario(scenarioId)
      return transformApiData(data)
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// Async thunk for creating a new test case
export const createTestCaseAsync = createAsyncThunk(
  'testScenario/createTestCase',
  async ({ testScenarioId, testCaseName, jiraProjectId, description, priority }, { rejectWithValue }) => {
    try {
      const data = await createTestCase(testScenarioId, testCaseName, jiraProjectId, description, priority)
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// Async thunk for creating a new test step
export const createTestStepAsync = createAsyncThunk(
  'testScenario/createTestStep',
  async ({ testCaseId, stepAction }, { rejectWithValue }) => {
    try {
      const data = await createTestStep(testCaseId, stepAction)
      return { testCaseId, stepData: data }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// Async thunk for updating a test case
export const updateTestCaseAsync = createAsyncThunk(
  'testScenario/updateTestCase',
  async ({ testCaseId, updates }, { rejectWithValue }) => {
    try {
      const data = await updateTestCase(testCaseId, updates)
      return { testCaseId, updates: data }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// Async thunk for updating a test step
export const updateTestStepAsync = createAsyncThunk(
  'testScenario/updateTestStep',
  async ({ testCaseId, testStepId, updates }, { rejectWithValue }) => {
    try {
      const data = await updateTestStep(testCaseId, testStepId, updates)
      return { testCaseId, testStepId, updates: data }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

// Async thunk for updating test case execution status
export const updateTestCaseExecutionAsync = createAsyncThunk(
  'testScenario/updateTestCaseExecution',
  async ({ testScenarioId, testRunId, testCaseId, statusId, statusName, statusColor }, { rejectWithValue }) => {
    try {
      await updateTestCaseExecution(testScenarioId, testRunId, testCaseId, statusId)
      return { testCaseId, runId: testRunId, statusId, statusName, statusColor }
    } catch (error) {
      // Extract error message from response
      const errorMessage = error.response?.data?.message
        || error.message
        || 'Failed to update execution status'

      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status,
        statusText: error.response?.statusText
      })
    }
  }
)

// Async thunk for updating test step execution status
export const updateTestStepExecutionAsync = createAsyncThunk(
  'testScenario/updateTestStepExecution',
  async ({ testScenarioId, testRunId, testCaseId, testStepId, statusId, statusName, statusColor }, { rejectWithValue }) => {
    try {
      await updateTestStepExecution(testScenarioId, testRunId, testStepId, statusId)
      return { testCaseId, stepId: testStepId, runId: testRunId, statusId, statusName, statusColor }
    } catch (error) {
      // Extract error message from response
      const errorMessage = error.response?.data?.message
        || error.message
        || 'Failed to update execution status'

      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status,
        statusText: error.response?.statusText
      })
    }
  }
)

/**
 * ASYNC THUNK: UPDATE TEST SCENARIO
 *
 * Updates test scenario name, description, and/or priority via API
 * Then updates Redux state with new values
 */
export const updateTestScenarioAsync = createAsyncThunk(
  'testScenario/updateTestScenario',
  async ({ testScenarioId, updates }, { rejectWithValue }) => {
    try {
      const data = await updateTestScenario(testScenarioId, updates)
      // Merge API response with original updates to preserve _assigneeObject
      // (API doesn't return _assigneeObject, but reducer needs it for state update)
      return {
        updates: {
          ...data,
          _assigneeObject: updates._assigneeObject
        }
      }
    } catch (error) {
      // Extract error message from response
      const errorMessage = error.response?.data?.message
        || error.message
        || 'Failed to update test scenario'

      return rejectWithValue({
        message: errorMessage,
        status: error.response?.status,
        statusText: error.response?.statusText
      })
    }
  }
)

/**
 * ASYNC THUNK: FETCH PROJECT LABELS
 *
 * Fetches all available labels in the project for autocomplete
 */
export const fetchProjectLabelsAsync = createAsyncThunk(
  'testScenario/fetchProjectLabels',
  async (projectId, { rejectWithValue }) => {
    try {
      return await fetchProjectLabels(projectId)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to fetch project labels',
        status: error.response?.status
      })
    }
  }
)

/**
 * ASYNC THUNK: FETCH TEST SCENARIO LABELS
 *
 * Fetches labels currently assigned to the test scenario
 */
export const fetchTestScenarioLabelsAsync = createAsyncThunk(
  'testScenario/fetchTestScenarioLabels',
  async (testScenarioId, { rejectWithValue }) => {
    try {
      return await fetchTestScenarioLabels(testScenarioId)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to fetch test scenario labels',
        status: error.response?.status
      })
    }
  }
)

/**
 * ASYNC THUNK: ADD TEST SCENARIO LABEL
 *
 * Adds a label to test scenario (existing or creates new)
 * Mixed flow: accepts { labelId } for existing or { labelName } for new
 */
export const addTestScenarioLabelAsync = createAsyncThunk(
  'testScenario/addTestScenarioLabel',
  async ({ testScenarioId, labelData }, { rejectWithValue }) => {
    try {
      return await addTestScenarioLabel(testScenarioId, labelData)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to add label',
        status: error.response?.status
      })
    }
  }
)

/**
 * ASYNC THUNK: REMOVE TEST SCENARIO LABEL
 *
 * Removes a label from test scenario
 */
export const removeTestScenarioLabelAsync = createAsyncThunk(
  'testScenario/removeTestScenarioLabel',
  async ({ testScenarioId, labelId }, { rejectWithValue }) => {
    try {
      await removeTestScenarioLabel(testScenarioId, labelId)
      return { labelId } // Return for optimistic update
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to remove label',
        status: error.response?.status
      })
    }
  }
)

/**
 * ============================================================================
 * TEST RUN CUSTOM FIELDS ASYNC THUNKS
 * ============================================================================
 */

/**
 * ASYNC THUNK: FETCH TEST RUN CUSTOM FIELDS
 *
 * Fetches custom field definitions for test runs in a project.
 * Fields are used to dynamically render the run header and create run modal.
 */
export const fetchTestRunCustomFieldsAsync = createAsyncThunk(
  'testScenario/fetchTestRunCustomFields',
  async (projectId, { rejectWithValue }) => {
    try {
      const data = await fetchTestRunCustomFields(projectId)
      return data
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to fetch custom fields',
        status: error.response?.status
      })
    }
  }
)

/**
 * ASYNC THUNK: CREATE TEST RUN
 *
 * Creates a new test run for a test scenario.
 * Field values are passed as key-value pairs where keys are fieldName.
 */
export const createTestRunAsync = createAsyncThunk(
  'testScenario/createTestRun',
  async ({ testScenarioId, runData }, { rejectWithValue }) => {
    try {
      const data = await createTestRun(testScenarioId, runData)
      return data
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to create test run',
        status: error.response?.status
      })
    }
  }
)

/**
 * ASYNC THUNK: UPDATE TEST RUN
 *
 * Updates field values for an existing test run.
 * Field values are passed as key-value pairs where keys are fieldName.
 */
export const updateTestRunAsync = createAsyncThunk(
  'testScenario/updateTestRun',
  async ({ testScenarioId, testRunId, updates }, { rejectWithValue }) => {
    try {
      const data = await updateTestRun(testScenarioId, testRunId, updates)
      // Return both API response and original updates to ensure null values are applied
      return { testRunId, data, updates }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to update test run',
        status: error.response?.status
      })
    }
  }
)

/**
 * ASYNC THUNK: DELETE TEST RUN
 *
 * Permanently deletes a test run and removes it from Redux state.
 * Also clears all execution data for this run from test cases/steps.
 */
export const deleteTestRunAsync = createAsyncThunk(
  'testScenario/deleteTestRun',
  async ({ testScenarioId, testRunId }, { rejectWithValue }) => {
    try {
      await deleteTestRun(testScenarioId, testRunId)
      return { testRunId }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to delete test run',
        status: error.response?.status
      })
    }
  }
)

/**
 * ASYNC THUNK: CLEAR TEST RUN
 *
 * Clears all execution statuses for a test run.
 * Preserves run structure but resets all test case/step executions to null.
 */
export const clearTestRunAsync = createAsyncThunk(
  'testScenario/clearTestRun',
  async ({ testScenarioId, testRunId }, { rejectWithValue }) => {
    try {
      await clearTestRun(testScenarioId, testRunId)
      return { testRunId }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to clear test run',
        status: error.response?.status
      })
    }
  }
)

/**
 * ASYNC THUNK: TOGGLE RUN COMPLETED STATUS
 *
 * Marks a run as completed or active.
 * When marking completed, auto-sets completedAt timestamp.
 */
export const toggleRunCompletedAsync = createAsyncThunk(
  'testScenario/toggleRunCompleted',
  async ({ testScenarioId, testRunId, isCompleted }, { rejectWithValue }) => {
    try {
      // Build updates object - only send isCompleted, backend handles completedAt
      const updates = {
        isCompleted
      }

      const data = await updateTestRun(testScenarioId, testRunId, updates)
      return { testRunId, data, isCompleted }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to update run status',
        status: error.response?.status
      })
    }
  }
)

/**
 * ============================================================================
 * CUSTOM FIELD MANAGEMENT ASYNC THUNKS
 * ============================================================================
 */

/**
 * ASYNC THUNK: CREATE CUSTOM FIELD
 *
 * Creates a new custom field for test runs (TEXT type by default)
 */
export const createCustomFieldAsync = createAsyncThunk(
  'testScenario/createCustomField',
  async ({ fieldName, jiraProjectId }, { rejectWithValue }) => {
    try {
      const data = await createCustomField(fieldName, jiraProjectId)
      return data
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to create custom field',
        status: error.response?.status
      })
    }
  }
)

/**
 * ASYNC THUNK: UPDATE CUSTOM FIELD
 *
 * Updates a custom field's properties (fieldName, showField, orderIndex)
 */
export const updateCustomFieldAsync = createAsyncThunk(
  'testScenario/updateCustomField',
  async ({ fieldId, jiraProjectId, updates }, { rejectWithValue }) => {
    try {
      const data = await updateCustomField(fieldId, jiraProjectId, updates)
      return { fieldId, updates: data }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to update custom field',
        status: error.response?.status
      })
    }
  }
)

/**
 * ASYNC THUNK: DELETE CUSTOM FIELD
 *
 * Deletes a custom field (only non-default fields can be deleted)
 */
export const deleteCustomFieldAsync = createAsyncThunk(
  'testScenario/deleteCustomField',
  async ({ fieldId, jiraProjectId }, { rejectWithValue }) => {
    try {
      await deleteCustomField(fieldId, jiraProjectId)
      return { fieldId }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to delete custom field',
        status: error.response?.status
      })
    }
  }
)

/**
 * ASYNC THUNK: BULK UPDATE CUSTOM FIELDS
 *
 * Updates multiple custom fields in parallel (for reordering and visibility changes)
 * Used when saving all changes from the CustomFieldsModal
 */
export const bulkUpdateCustomFieldsAsync = createAsyncThunk(
  'testScenario/bulkUpdateCustomFields',
  async ({ jiraProjectId, updates }, { rejectWithValue }) => {
    try {
      // updates is array of { fieldId, orderIndex?, showField? }
      const results = await Promise.all(
        updates.map(({ fieldId, ...fieldUpdates }) =>
          updateCustomField(fieldId, jiraProjectId, fieldUpdates)
        )
      )
      return results
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to update custom fields',
        status: error.response?.status
      })
    }
  }
)

/**
 * ============================================================================
 * DELETE TEST CASE AND TEST STEP ASYNC THUNKS
 * ============================================================================
 */

/**
 * ASYNC THUNK: DELETE TEST CASE
 *
 * Deletes a test case and all its associated test steps.
 * Removes the test case from Redux state on success.
 */
export const deleteTestCaseAsync = createAsyncThunk(
  'testScenario/deleteTestCase',
  async ({ testCaseId }, { rejectWithValue }) => {
    try {
      await deleteTestCase(testCaseId)
      return { testCaseId }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to delete test case',
        status: error.response?.status
      })
    }
  }
)

/**
 * ASYNC THUNK: DELETE TEST STEP
 *
 * Deletes a test step from a test case.
 * Removes the test step from Redux state and renumbers remaining steps on success.
 */
export const deleteTestStepAsync = createAsyncThunk(
  'testScenario/deleteTestStep',
  async ({ testCaseId, testStepId }, { rejectWithValue }) => {
    try {
      await deleteTestStep(testCaseId, testStepId)
      return { testCaseId, testStepId }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to delete test step',
        status: error.response?.status
      })
    }
  }
)

// ============================================================================
// LINKED JIRA ISSUES ASYNC THUNKS
// ============================================================================

/**
 * FETCH LINKED JIRA ISSUES
 * Fetches all Jira issues linked to the current test scenario.
 */
export const fetchLinkedJiraIssuesAsync = createAsyncThunk(
  'testScenario/fetchLinkedJiraIssues',
  async (testScenarioId, { rejectWithValue }) => {
    try {
      return await fetchLinkedIssuesForScenario(testScenarioId)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to fetch linked issues',
        status: error.response?.status
      })
    }
  }
)

/**
 * LINK JIRA ISSUE TO SCENARIO
 * Links a single Jira issue to the current test scenario.
 */
export const linkJiraIssueAsync = createAsyncThunk(
  'testScenario/linkJiraIssue',
  async ({ testScenarioId, jiraIssueKey }, { rejectWithValue }) => {
    try {
      return await linkJiraIssueToScenario(testScenarioId, jiraIssueKey)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to link Jira issue',
        status: error.response?.status
      })
    }
  }
)

/**
 * UNLINK JIRA ISSUE FROM SCENARIO
 * Removes a Jira issue link from the current test scenario.
 */
export const unlinkJiraIssueAsync = createAsyncThunk(
  'testScenario/unlinkJiraIssue',
  async ({ testScenarioId, linkId }, { rejectWithValue }) => {
    try {
      await unlinkJiraIssueApi(testScenarioId, linkId)
      return { linkId }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to unlink Jira issue',
        status: error.response?.status
      })
    }
  }
)

const initialState = {
  testScenarioId: null,
  scenarioKey: '', // Jira issue key (e.g., "KAN-1")
  testScenarioName: '',
  testScenarioDescription: '', // ADD: Test scenario description from API
  testScenarioPriority: 'Medium', // ADD: Test scenario priority (Low, Medium, High, Critical)
  testScenarioAssignee: null, // ADD: Test scenario assignee { accountId, displayName, avatarUrl }
  testScenarioLabels: [], // ADD: Labels assigned to test scenario [{ id, name }]
  projectLabels: [], // ADD: All available labels in project for autocomplete [{ id, name }]
  testRunCustomFields: [], // Custom field definitions for test runs [{ id, fieldName, fieldType, orderIndex, showField, isDefault }]
  linkedJiraIssues: [], // Jira issues linked to this scenario [{ id, jiraIssueKey, jiraIssueId, linkedByUserId, linkedOn }]
  linkedJiraIssuesLoading: false, // Loading state for linked issues fetch
  testRuns: [],
  testCases: [],
  expandedTests: [], // Which test cases are expanded (restored from localStorage)
  expandedSteps: [], // Which test steps have expanded details - testData/expected (restored from localStorage)
  loading: false,
  error: null
}

const testScenarioSlice = createSlice({
  name: 'testScenario',
  initialState,
  reducers: {
    // Toggle a single test case's expanded/collapsed state
    toggleTest: (state, action) => {
      const testId = action.payload
      if (state.expandedTests.includes(testId)) {
        state.expandedTests = state.expandedTests.filter(id => id !== testId)
      } else {
        state.expandedTests.push(testId)
      }
    },

    /**
     * SET EXPANDED TESTS
     * Replaces the entire expandedTests array.
     * Used to restore persisted state from localStorage on page load.
     */
    setExpandedTests: (state, action) => {
      state.expandedTests = action.payload
    },

    /**
     * TOGGLE STEP EXPAND
     * Toggles a test step's expanded/collapsed state for showing testData/expected content.
     * Mirrors toggleTest pattern but for step-level detail visibility.
     */
    toggleStepExpand: (state, action) => {
      const stepId = action.payload
      if (state.expandedSteps.includes(stepId)) {
        state.expandedSteps = state.expandedSteps.filter(id => id !== stepId)
      } else {
        state.expandedSteps.push(stepId)
      }
    },

    /**
     * SET EXPANDED STEPS
     * Replaces the entire expandedSteps array.
     * Used to restore persisted state from localStorage on page load.
     */
    setExpandedSteps: (state, action) => {
      state.expandedSteps = action.payload
    },

    updateExecution: (state, action) => {
      const { testCaseId, runId, statusId, statusName, statusColor } = action.payload
      
      const testCase = state.testCases.find(tc => tc.id === testCaseId)
      if (testCase) {
        if (!testCase.executions) {
          testCase.executions = {}
        }
        
        testCase.executions[runId] = {
          statusId,
          statusName,
          statusColor,
          executionTime: new Date().toISOString()
        }
      }
    },
    updateStepExecution: (state, action) => {
      const { testCaseId, stepId, runId, statusId, statusName, statusColor } = action.payload
      
      const testCase = state.testCases.find(tc => tc.id === testCaseId)
      if (testCase && testCase.testSteps) {
        const step = testCase.testSteps.find(s => s.id === stepId)
        if (step) {
          if (!step.executions) {
            step.executions = {}
          }
          
          step.executions[runId] = {
            statusId,
            statusName,
            statusColor,
            executionTime: new Date().toISOString()
          }
        }
      }
    },
    addNewRun: (state, action) => {
      const newRun = action.payload
      state.testRuns.push(newRun)
    },
    clearError: (state) => {
      state.error = null
    },
    reorderTestCases: (state, action) => {
      const { activeId, overId } = action.payload
      // Convert to both string and number for comparison (handle ID type mismatch)
      const activeIdStr = String(activeId)
      const activeIdNum = Number(activeId)
      const overIdStr = String(overId)
      const overIdNum = Number(overId)
      
      const activeIndex = state.testCases.findIndex(tc => 
        tc.id === activeIdStr || tc.id === activeIdNum || String(tc.id) === activeIdStr
      )
      const overIndex = state.testCases.findIndex(tc => 
        tc.id === overIdStr || tc.id === overIdNum || String(tc.id) === overIdStr
      )
      
      console.log('reorderTestCases reducer:', { 
        activeId, 
        overId, 
        activeIndex, 
        overIndex,
        testCaseIds: state.testCases.map(tc => ({ id: tc.id, type: typeof tc.id }))
      })
      
      if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
        const [movedItem] = state.testCases.splice(activeIndex, 1)
        state.testCases.splice(overIndex, 0, movedItem)
        
        // Update orderIndex for all test cases (optimistic update)
        state.testCases.forEach((tc, index) => {
          tc.orderIndex = index + 1
          tc.number = (index + 1).toString()        })
        
        console.log('After reorder:', state.testCases.map(tc => ({ id: tc.id, orderIndex: tc.orderIndex })))
      } else {
        console.log('reorderTestCases: Could not find items or same position', { activeIndex, overIndex })
      }
    },
    reorderTestSteps: (state, action) => {
      const { testCaseId, activeId, overId } = action.payload
      // Convert to both string and number for comparison (handle ID type mismatch)
      const testCaseIdStr = String(testCaseId)
      const testCaseIdNum = Number(testCaseId)
      const activeIdStr = String(activeId)
      const activeIdNum = Number(activeId)
      const overIdStr = String(overId)
      const overIdNum = Number(overId)
      
      const testCase = state.testCases.find(tc => 
        tc.id === testCaseIdStr || tc.id === testCaseIdNum || String(tc.id) === testCaseIdStr
      )
      
      if (testCase && testCase.testSteps) {
        const activeIndex = testCase.testSteps.findIndex(s => 
          s.id === activeIdStr || s.id === activeIdNum || String(s.id) === activeIdStr
        )
        const overIndex = testCase.testSteps.findIndex(s => 
          s.id === overIdStr || s.id === overIdNum || String(s.id) === overIdStr
        )
        
        if (activeIndex !== -1 && overIndex !== -1 && activeIndex !== overIndex) {
          const [movedStep] = testCase.testSteps.splice(activeIndex, 1)
          testCase.testSteps.splice(overIndex, 0, movedStep)
          
          // Update orderIndex for all steps in this test case (optimistic update)
          testCase.testSteps.forEach((step, index) => {
            step.orderIndex = index + 1
            step.number = (index + 1).toString()          })
        }
      }
    },

    /**
     * ENRICH TEST SCENARIO ASSIGNEE
     *
     * Updates the assignee with full user details fetched from Jira.
     * Called after scenario loads when assignee has only accountId (no displayName).
     *
     * @param {Object} action.payload - { accountId, displayName, avatarUrl }
     */
    enrichTestScenarioAssignee: (state, action) => {
      const enrichedAssignee = action.payload
      if (enrichedAssignee && state.testScenarioAssignee) {
        // Only update if accountId matches (safety check)
        if (state.testScenarioAssignee.accountId === enrichedAssignee.accountId) {
          state.testScenarioAssignee = enrichedAssignee
        }
      }
    },

    /**
     * OPTIMISTIC: REMOVE TEST RUN
     *
     * Immediately removes run from UI before API call completes.
     * Called by App.jsx before dispatching deleteTestRunAsync.
     */
    removeTestRunOptimistic: (state, action) => {
      const { testRunId } = action.payload
      const runIdStr = String(testRunId)
      const runIdNum = Number(testRunId)

      // Remove from testRuns array
      state.testRuns = state.testRuns.filter(
        run => run.id !== testRunId && run.id !== runIdNum && String(run.id) !== runIdStr
      )

      // Clear executions for this run from all test cases and steps
      state.testCases.forEach(tc => {
        if (tc.executions) {
          delete tc.executions[runIdStr]
          delete tc.executions[runIdNum]
        }
        if (tc.testSteps) {
          tc.testSteps.forEach(step => {
            if (step.executions) {
              delete step.executions[runIdStr]
              delete step.executions[runIdNum]
            }
          })
        }
      })
    },

    /**
     * OPTIMISTIC: CLEAR RUN EXECUTIONS
     *
     * Immediately clears all execution statuses for a run.
     * Called by App.jsx before dispatching clearTestRunAsync.
     */
    clearRunExecutionsOptimistic: (state, action) => {
      const { testRunId } = action.payload
      const runIdStr = String(testRunId)
      const runIdNum = Number(testRunId)

      // Clear executions for this run from all test cases and steps
      state.testCases.forEach(tc => {
        if (tc.executions) {
          if (tc.executions[runIdStr]) {
            tc.executions[runIdStr] = { statusId: null, statusName: null, statusColor: null }
          }
          if (tc.executions[runIdNum]) {
            tc.executions[runIdNum] = { statusId: null, statusName: null, statusColor: null }
          }
        }
        if (tc.testSteps) {
          tc.testSteps.forEach(step => {
            if (step.executions) {
              if (step.executions[runIdStr]) {
                step.executions[runIdStr] = { statusId: null, statusName: null, statusColor: null }
              }
              if (step.executions[runIdNum]) {
                step.executions[runIdNum] = { statusId: null, statusName: null, statusColor: null }
              }
            }
          })
        }
      })
    },

    /**
     * OPTIMISTIC: TOGGLE RUN COMPLETED
     *
     * Immediately updates isCompleted status in UI.
     * Called by App.jsx before dispatching toggleRunCompletedAsync.
     */
    toggleRunCompletedOptimistic: (state, action) => {
      const { testRunId, isCompleted } = action.payload
      const run = state.testRuns.find(
        r => r.id === testRunId || r.id === Number(testRunId) || String(r.id) === String(testRunId)
      )
      if (run) {
        run.isCompleted = isCompleted
        if (isCompleted) {
          run.completedAt = new Date().toISOString()
        } else {
          run.completedAt = null
        }
      }
    },

    /**
     * UPDATE TEST CASE METADATA COUNT
     * Increments or decrements commentCount/attachmentCount on a test case.
     * Called from TestCaseEditModal after successful API operations.
     * @param {Object} action.payload - { testCaseId, field, delta }
     */
    updateTestCaseMetadataCount: (state, action) => {
      const { testCaseId, field, delta } = action.payload
      const testCase = state.testCases.find(
        tc => tc.id === testCaseId || tc.id === Number(testCaseId)
      )
      if (testCase) {
        testCase[field] = Math.max(0, (testCase[field] || 0) + delta)
      }
    },

    /**
     * UPDATE TEST STEP METADATA COUNT
     * Increments or decrements commentCount/attachmentCount on a test step.
     * Called from TestStepEditModal after successful API operations.
     * @param {Object} action.payload - { testCaseId, testStepId, field, delta }
     */
    updateTestStepMetadataCount: (state, action) => {
      const { testCaseId, testStepId, field, delta } = action.payload
      const testCase = state.testCases.find(
        tc => tc.id === testCaseId || tc.id === Number(testCaseId)
      )
      if (testCase && testCase.testSteps) {
        const step = testCase.testSteps.find(
          s => s.id === testStepId || s.id === Number(testStepId)
        )
        if (step) {
          step[field] = Math.max(0, (step[field] || 0) + delta)
        }
      }
    },

    /**
     * UPDATE EXECUTION METADATA COUNT
     * Increments or decrements commentCount/bugCount/attachmentCount on an execution.
     * Called from ExecutionDetailsModal after successful API operations.
     * @param {Object} action.payload - { testCaseId, testStepId, runId, field, delta }
     */
    updateExecutionMetadataCount: (state, action) => {
      const { testCaseId, testStepId, runId, field, delta } = action.payload
      const testCase = state.testCases.find(
        tc => tc.id === testCaseId || tc.id === Number(testCaseId)
      )
      if (!testCase) return

      let execution
      if (testStepId) {
        // Test step execution
        const step = testCase.testSteps?.find(
          s => s.id === testStepId || s.id === Number(testStepId)
        )
        if (step && step.executions) {
          execution = step.executions[runId] || step.executions[String(runId)]
        }
      } else {
        // Test case execution
        if (testCase.executions) {
          execution = testCase.executions[runId] || testCase.executions[String(runId)]
        }
      }

      if (execution) {
        execution[field] = Math.max(0, (execution[field] || 0) + delta)
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTestScenarioData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchTestScenarioData.fulfilled, (state, action) => {
        state.loading = false
        state.testScenarioId = action.payload.testScenarioId
        state.scenarioKey = action.payload.scenarioKey || '' // Jira issue key (e.g., "KAN-1")
        state.testScenarioName = action.payload.testScenarioName
        state.testScenarioDescription = action.payload.testScenarioDescription || '' // ADD: Populate description from API
        state.testScenarioPriority = action.payload.testScenarioPriority || 'Medium' // ADD: Populate priority from API
        state.testScenarioAssignee = action.payload.testScenarioAssignee || null // ADD: Populate assignee from API
        state.testRuns = action.payload.testRuns
        state.testCases = action.payload.testCases
        // Populate linked Jira issues from main API response (no separate fetch needed)
        state.linkedJiraIssues = action.payload.jiraIssues || []
        state.linkedJiraIssuesLoading = false
        state.error = null
      })
      .addCase(fetchTestScenarioData.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(createTestCaseAsync.pending, (state) => {
        // No loading state for create operations
      })
      .addCase(createTestCaseAsync.fulfilled, (state, action) => {
        // Add the new test case to the list
        const newTestCase = {
          id: action.payload.id,
          type: 'test',
          number: (state.testCases.length + 1).toString(),
          title: action.payload.testCaseName,
          description: action.payload.description || '',
          priority: action.payload.priority,
          assignee: action.payload.assignee?.accountId || 'Unassigned',
          tags: action.payload.labels?.map(label => label.name) || [],
          steps: 0,
          attachments: 0,
          expanded: false,
          testSteps: [],
          executions: {}
        }
        state.testCases.push(newTestCase)
      })
      .addCase(createTestCaseAsync.rejected, (state, action) => {
        state.error = action.payload
      })
      .addCase(createTestStepAsync.pending, (state) => {
        // No loading state for create operations
      })
      .addCase(createTestStepAsync.fulfilled, (state, action) => {
        const { testCaseId, stepData } = action.payload
        const testCase = state.testCases.find(tc => tc.id === testCaseId)
        if (testCase) {
          if (!testCase.testSteps) {
            testCase.testSteps = []
          }
          // Add the new test step to the test case
          const newStep = {
            id: stepData.id || Date.now(), // Use API ID or fallback to timestamp
            number: (testCase.testSteps.length + 1).toString(),
            title: stepData.stepAction || stepData.title || 'New Step',
            testData: stepData.testData || '',
            expected: stepData.expected || '',
            attachments: stepData.attachments || 0,
            executions: {}
          }
          testCase.testSteps.push(newStep)
          
          // If this is the first step, make sure the test case is expanded
          // Auto-expand the test case when adding a step if it's collapsed
          if (!state.expandedTests.includes(testCaseId)) {
            state.expandedTests.push(testCaseId)
          }
        }
      })
      .addCase(createTestStepAsync.rejected, (state, action) => {
        state.error = action.payload
      })
      .addCase(updateTestCaseAsync.fulfilled, (state, action) => {
        const { testCaseId, updates } = action.payload
        const testCase = state.testCases.find(tc => tc.id === testCaseId)
        if (testCase) {
          // Update only the fields that were changed
          if (updates.testCaseName) testCase.title = updates.testCaseName
          if (updates.description !== undefined) testCase.description = updates.description
          if (updates.priority) testCase.priority = updates.priority
          if (updates.orderIndex !== undefined) {
            testCase.orderIndex = updates.orderIndex
            testCase.number = updates.orderIndex.toString()          }
        }
      })
      .addCase(updateTestCaseAsync.rejected, (state, action) => {
        state.error = action.payload
      })
      .addCase(updateTestStepAsync.fulfilled, (state, action) => {
        const { testCaseId, testStepId, updates } = action.payload
        const testCase = state.testCases.find(tc => tc.id === testCaseId)
        if (testCase && testCase.testSteps) {
          const step = testCase.testSteps.find(s => s.id === testStepId)
          if (step) {
            // Update only the fields that were changed
            if (updates.stepAction) step.title = updates.stepAction
            if (updates.testData !== undefined) step.testData = updates.testData
            if (updates.expectedResult !== undefined) step.expected = updates.expectedResult
            if (updates.orderIndex !== undefined) {
              step.orderIndex = updates.orderIndex
              step.number = updates.orderIndex.toString()            }
          }
        }
      })
      .addCase(updateTestStepAsync.rejected, (state, action) => {
        state.error = action.payload
      })
      .addCase(updateTestCaseExecutionAsync.fulfilled, (state, action) => {
        const { testCaseId, runId, statusId, statusName, statusColor } = action.payload
        const testCase = state.testCases.find(tc => tc.id === testCaseId)
        if (testCase) {
          if (!testCase.executions) {
            testCase.executions = {}
          }
          // Preserve existing execution data (especially id/executionId) while updating status
          const existingExecution = testCase.executions[runId] || {}
          testCase.executions[runId] = {
            ...existingExecution,
            statusId,
            statusName,
            statusColor,
            executionTime: new Date().toISOString()
          }

          // CASCADE: When test case is set to "Pass", automatically update child test steps
          // that have blank (no execution) or "Fail"/"Failed" status to "Pass" as well.
          // This is a UI-only cascade - the backend handles persistence separately.
          // Steps with other statuses (Pass, Block, Exclude, etc.) are left untouched.
          const isPassStatus = statusName?.toLowerCase() === 'pass' || statusName?.toLowerCase() === 'passed'
          if (isPassStatus && testCase.testSteps && testCase.testSteps.length > 0) {
            testCase.testSteps.forEach(step => {
              if (!step.executions) {
                step.executions = {}
              }
              const stepExecution = step.executions[runId]
              const stepStatusName = stepExecution?.statusName?.toLowerCase()

              // Only cascade to steps that are blank (no result) or failed
              const isBlank = !stepExecution || !stepExecution.statusName
              const isFail = stepStatusName === 'fail' || stepStatusName === 'failed'

              if (isBlank || isFail) {
                const existingStepExecution = step.executions[runId] || {}
                step.executions[runId] = {
                  ...existingStepExecution,
                  statusId,
                  statusName,
                  statusColor,
                  executionTime: new Date().toISOString()
                }
              }
            })
          }
        }
      })
      .addCase(updateTestCaseExecutionAsync.rejected, (state, action) => {
        // Don't set state.error - errors are shown via notifications instead
        // This prevents the full-screen error page from appearing on execution update failures
        console.error('Test case execution update failed:', action.payload)
      })
      .addCase(updateTestStepExecutionAsync.fulfilled, (state, action) => {
        const { testCaseId, stepId, runId, statusId, statusName, statusColor } = action.payload
        const testCase = state.testCases.find(tc => tc.id === testCaseId)
        if (testCase && testCase.testSteps) {
          const step = testCase.testSteps.find(s => s.id === stepId)
          if (step) {
            if (!step.executions) {
              step.executions = {}
            }
            // Preserve existing execution data (especially id/executionId) while updating status
            const existingExecution = step.executions[runId] || {}
            step.executions[runId] = {
              ...existingExecution,
              statusId,
              statusName,
              statusColor,
              executionTime: new Date().toISOString()
            }
          }
        }
      })
      .addCase(updateTestStepExecutionAsync.rejected, (state, action) => {
        // Don't set state.error - errors are shown via notifications instead
        // This prevents the full-screen error page from appearing on execution update failures
        console.error('Test step execution update failed:', action.payload)
      })
      .addCase(updateTestScenarioAsync.fulfilled, (state, action) => {
        const { updates } = action.payload

        // Update test scenario name if provided
        if (updates.scenarioName !== undefined) {
          state.testScenarioName = updates.scenarioName
        }

        // Update description if provided
        if (updates.description !== undefined) {
          state.testScenarioDescription = updates.description
        }

        // Update priority if provided
        if (updates.priority !== undefined) {
          state.testScenarioPriority = updates.priority
        }

        // Update assignee if provided
        // Check for _assigneeObject (full object from modal) or fallback to assignee field
        if (updates._assigneeObject !== undefined) {
          // Full assignee object from modal update
          state.testScenarioAssignee = updates._assigneeObject
        } else if (updates.assignee !== undefined) {
          // Backward compatibility - if only accountId is provided
          // This can happen if API returns just the accountId
          if (typeof updates.assignee === 'object') {
            state.testScenarioAssignee = updates.assignee
          } else if (updates.assignee === null) {
            state.testScenarioAssignee = null
          }
          // If it's just a string accountId, we keep existing state
          // (the full object will be fetched on next data load)
        }
      })
      .addCase(updateTestScenarioAsync.rejected, (state, action) => {
        // Don't set state.error - errors are shown via notifications instead
        console.error('Test scenario update failed:', action.payload)
      })

      // LABEL ASYNC THUNK REDUCERS

      // Fetch project labels
      .addCase(fetchProjectLabelsAsync.fulfilled, (state, action) => {
        state.projectLabels = action.payload || []
      })
      .addCase(fetchProjectLabelsAsync.rejected, (state, action) => {
        console.error('Failed to fetch project labels:', action.payload)
      })

      // Fetch test scenario labels
      .addCase(fetchTestScenarioLabelsAsync.fulfilled, (state, action) => {
        state.testScenarioLabels = action.payload || []
      })
      .addCase(fetchTestScenarioLabelsAsync.rejected, (state, action) => {
        console.error('Failed to fetch test scenario labels:', action.payload)
      })

      // Add label
      .addCase(addTestScenarioLabelAsync.fulfilled, (state, action) => {
        const label = action.payload
        // Add to scenario labels (avoid duplicates)
        if (!state.testScenarioLabels.find(l => l.id === label.id)) {
          state.testScenarioLabels.push(label)
        }
        // Add to project labels if new (for future autocomplete)
        if (!state.projectLabels.find(l => l.id === label.id)) {
          state.projectLabels.push(label)
        }
      })
      .addCase(addTestScenarioLabelAsync.rejected, (state, action) => {
        console.error('Failed to add label:', action.payload)
      })

      // Remove label
      .addCase(removeTestScenarioLabelAsync.fulfilled, (state, action) => {
        const { labelId } = action.payload
        state.testScenarioLabels = state.testScenarioLabels.filter(
          l => l.id !== labelId
        )
      })
      .addCase(removeTestScenarioLabelAsync.rejected, (state, action) => {
        console.error('Failed to remove label:', action.payload)
      })

      // ========================================================================
      // TEST RUN CUSTOM FIELDS REDUCERS
      // ========================================================================

      // Fetch custom fields
      .addCase(fetchTestRunCustomFieldsAsync.fulfilled, (state, action) => {
        state.testRunCustomFields = action.payload || []
      })
      .addCase(fetchTestRunCustomFieldsAsync.rejected, (state, action) => {
        console.error('Failed to fetch custom fields:', action.payload)
      })

      // Create test run
      .addCase(createTestRunAsync.fulfilled, (state, action) => {
        // Add the new run to testRuns array
        const newRun = action.payload
        state.testRuns.push(newRun)
      })
      .addCase(createTestRunAsync.rejected, (state, action) => {
        console.error('Failed to create test run:', action.payload)
      })

      // Update test run
      .addCase(updateTestRunAsync.fulfilled, (state, action) => {
        // Update the run in testRuns array with new field values
        const { testRunId, data, updates } = action.payload
        const runIndex = state.testRuns.findIndex(run => run.id === testRunId || run.id === Number(testRunId))
        if (runIndex !== -1) {
          // Merge the updated data into the existing run
          // Also apply original updates to ensure null values are set (API may omit null fields)
          state.testRuns[runIndex] = { ...state.testRuns[runIndex], ...data, ...updates }
        }
      })
      .addCase(updateTestRunAsync.rejected, (state, action) => {
        console.error('Failed to update test run:', action.payload)
      })

      // ========================================================================
      // DELETE TEST RUN REDUCERS
      // ========================================================================

      // Delete test run - state already updated optimistically by removeTestRunOptimistic
      .addCase(deleteTestRunAsync.fulfilled, (state, action) => {
        // State already updated by optimistic reducer, just log success
        console.log('Test run deleted successfully:', action.payload.testRunId)
      })
      .addCase(deleteTestRunAsync.rejected, (state, action) => {
        // Rollback handled in component - may need to refetch data
        console.error('Failed to delete test run:', action.payload)
      })

      // ========================================================================
      // CLEAR TEST RUN REDUCERS
      // ========================================================================

      // Clear test run - state already updated optimistically by clearRunExecutionsOptimistic
      .addCase(clearTestRunAsync.fulfilled, (state, action) => {
        // State already updated by optimistic reducer, just log success
        console.log('Test run cleared successfully:', action.payload.testRunId)
      })
      .addCase(clearTestRunAsync.rejected, (state, action) => {
        // Rollback would require refetching data - handled in component
        console.error('Failed to clear test run:', action.payload)
      })

      // ========================================================================
      // TOGGLE RUN COMPLETED STATUS REDUCERS
      // ========================================================================

      .addCase(toggleRunCompletedAsync.fulfilled, (state, action) => {
        const { testRunId, data } = action.payload
        const runIndex = state.testRuns.findIndex(
          run => run.id === testRunId || run.id === Number(testRunId)
        )
        if (runIndex !== -1) {
          // Merge API response with existing run data
          state.testRuns[runIndex] = { ...state.testRuns[runIndex], ...data }
        }
      })
      .addCase(toggleRunCompletedAsync.rejected, (state, action) => {
        // Rollback handled in component via toggleRunCompletedOptimistic
        console.error('Failed to toggle run completed status:', action.payload)
      })

      // ========================================================================
      // CUSTOM FIELD MANAGEMENT REDUCERS
      // ========================================================================

      // Create custom field
      .addCase(createCustomFieldAsync.fulfilled, (state, action) => {
        // Add the new field to the array
        state.testRunCustomFields.push(action.payload)
        // Sort by orderIndex to maintain correct display order
        state.testRunCustomFields.sort((a, b) => a.orderIndex - b.orderIndex)
      })
      .addCase(createCustomFieldAsync.rejected, (state, action) => {
        console.error('Failed to create custom field:', action.payload)
      })

      // Update custom field
      .addCase(updateCustomFieldAsync.fulfilled, (state, action) => {
        const { fieldId, updates } = action.payload
        const fieldIndex = state.testRunCustomFields.findIndex(f => f.id === fieldId)
        if (fieldIndex !== -1) {
          // Merge updates with existing field
          state.testRunCustomFields[fieldIndex] = {
            ...state.testRunCustomFields[fieldIndex],
            ...updates
          }
        }
        // Re-sort by orderIndex after update
        state.testRunCustomFields.sort((a, b) => a.orderIndex - b.orderIndex)
      })
      .addCase(updateCustomFieldAsync.rejected, (state, action) => {
        console.error('Failed to update custom field:', action.payload)
      })

      // Delete custom field
      .addCase(deleteCustomFieldAsync.fulfilled, (state, action) => {
        const { fieldId } = action.payload
        state.testRunCustomFields = state.testRunCustomFields.filter(f => f.id !== fieldId)
      })
      .addCase(deleteCustomFieldAsync.rejected, (state, action) => {
        console.error('Failed to delete custom field:', action.payload)
      })

      // Bulk update custom fields (for reordering)
      .addCase(bulkUpdateCustomFieldsAsync.fulfilled, (state, action) => {
        // Update each field with the server response
        action.payload.forEach(updatedField => {
          const index = state.testRunCustomFields.findIndex(f => f.id === updatedField.id)
          if (index !== -1) {
            state.testRunCustomFields[index] = updatedField
          }
        })
        // Re-sort by orderIndex
        state.testRunCustomFields.sort((a, b) => a.orderIndex - b.orderIndex)
      })
      .addCase(bulkUpdateCustomFieldsAsync.rejected, (state, action) => {
        console.error('Failed to bulk update custom fields:', action.payload)
      })

      // ========================================================================
      // DELETE TEST CASE AND TEST STEP REDUCERS
      // ========================================================================

      // Delete test case - removes test case from state and from expandedTests
      .addCase(deleteTestCaseAsync.fulfilled, (state, action) => {
        const { testCaseId } = action.payload
        // Remove the test case from the array (handle both string and number ID types)
        state.testCases = state.testCases.filter(
          tc => tc.id !== testCaseId && tc.id !== Number(testCaseId)
        )
        // Remove from expandedTests if present
        state.expandedTests = state.expandedTests.filter(
          id => id !== testCaseId && id !== Number(testCaseId)
        )
        // Renumber remaining test cases to maintain sequential ordering
        state.testCases.forEach((tc, index) => {
          tc.orderIndex = index + 1
          tc.number = (index + 1).toString()        })
      })
      .addCase(deleteTestCaseAsync.rejected, (state, action) => {
        console.error('Failed to delete test case:', action.payload)
      })

      // Delete test step - removes test step from parent test case and renumbers remaining steps
      .addCase(deleteTestStepAsync.fulfilled, (state, action) => {
        const { testCaseId, testStepId } = action.payload
        // Find the parent test case (handle both string and number ID types)
        const testCase = state.testCases.find(
          tc => tc.id === testCaseId || tc.id === Number(testCaseId)
        )
        if (testCase && testCase.testSteps) {
          // Remove the test step from the array
          testCase.testSteps = testCase.testSteps.filter(
            s => s.id !== testStepId && s.id !== Number(testStepId)
          )
          // Renumber remaining steps to maintain sequential ordering
          testCase.testSteps.forEach((step, index) => {
            step.orderIndex = index + 1
            step.number = (index + 1).toString()          })
        }
        // Remove deleted step from expandedSteps to keep expansion state clean
        state.expandedSteps = state.expandedSteps.filter(
          id => id !== testStepId && id !== Number(testStepId)
        )
      })
      .addCase(deleteTestStepAsync.rejected, (state, action) => {
        console.error('Failed to delete test step:', action.payload)
      })

      // ========================================================================
      // LINKED JIRA ISSUES REDUCERS
      // ========================================================================

      // Fetch linked Jira issues
      .addCase(fetchLinkedJiraIssuesAsync.pending, (state) => {
        state.linkedJiraIssuesLoading = true
      })
      .addCase(fetchLinkedJiraIssuesAsync.fulfilled, (state, action) => {
        state.linkedJiraIssuesLoading = false
        state.linkedJiraIssues = action.payload || []
      })
      .addCase(fetchLinkedJiraIssuesAsync.rejected, (state, action) => {
        state.linkedJiraIssuesLoading = false
        console.error('Failed to fetch linked Jira issues:', action.payload)
      })

      // Link Jira issue - add to list on success
      .addCase(linkJiraIssueAsync.fulfilled, (state, action) => {
        const newLink = action.payload
        // Avoid duplicates by checking existing IDs
        if (!state.linkedJiraIssues.find(l => l.id === newLink.id)) {
          state.linkedJiraIssues.push(newLink)
        }
      })
      .addCase(linkJiraIssueAsync.rejected, (state, action) => {
        console.error('Failed to link Jira issue:', action.payload)
      })

      // Unlink Jira issue - remove from list on success
      .addCase(unlinkJiraIssueAsync.fulfilled, (state, action) => {
        const { linkId } = action.payload
        state.linkedJiraIssues = state.linkedJiraIssues.filter(
          l => l.id !== linkId && l.id !== Number(linkId)
        )
      })
      .addCase(unlinkJiraIssueAsync.rejected, (state, action) => {
        console.error('Failed to unlink Jira issue:', action.payload)
      })
  }
})

export const {
  toggleTest,
  setExpandedTests,
  toggleStepExpand,
  setExpandedSteps,
  updateExecution,
  updateStepExecution,
  addNewRun,
  clearError,
  reorderTestCases,
  reorderTestSteps,
  enrichTestScenarioAssignee,
  removeTestRunOptimistic,
  clearRunExecutionsOptimistic,
  toggleRunCompletedOptimistic,
  updateTestCaseMetadataCount,
  updateTestStepMetadataCount,
  updateExecutionMetadataCount
} = testScenarioSlice.actions

// Selectors
export const selectTestScenario = (state) => state.testScenario
export const selectTestCases = (state) => state.testScenario.testCases
export const selectTestRuns = (state) => state.testScenario.testRuns
export const selectExpandedTests = (state) => state.testScenario.expandedTests
export const selectExpandedSteps = (state) => state.testScenario.expandedSteps
export const selectLoading = (state) => state.testScenario.loading
export const selectError = (state) => state.testScenario.error

// ADD: Selector for test scenario details (used in edit modal)
export const selectTestScenarioDetails = (state) => ({
  testScenarioId: state.testScenario.testScenarioId,
  scenarioKey: state.testScenario.scenarioKey, // Jira issue key (e.g., "KAN-1")
  testScenarioName: state.testScenario.testScenarioName,
  testScenarioDescription: state.testScenario.testScenarioDescription,
  testScenarioPriority: state.testScenario.testScenarioPriority,
  testScenarioAssignee: state.testScenario.testScenarioAssignee
})

// ADD: Label selectors
export const selectTestScenarioLabels = (state) => state.testScenario.testScenarioLabels
export const selectProjectLabels = (state) => state.testScenario.projectLabels

// ADD: Test run custom fields selector
export const selectTestRunCustomFields = (state) => state.testScenario.testRunCustomFields

// Linked Jira issues selectors
export const selectLinkedJiraIssues = (state) => state.testScenario.linkedJiraIssues
export const selectLinkedJiraIssuesLoading = (state) => state.testScenario.linkedJiraIssuesLoading

export default testScenarioSlice.reducer
